/**
 * Preparing a shop's logo for printing — and for living inside a synced row.
 *
 * ── Where the logo is kept, and why ─────────────────────────────────────────
 * In `merchants.logo_url`, as a `data:` URI. Not in object storage, and the
 * reason is not thrift:
 *
 *   • Both print paths need the BYTES locally. The A4 template refuses any
 *     non-`data:` src because the print window has no network — an http(s)
 *     logo would simply be missing from every saved PDF. The thermal path
 *     rasterises to ESC/POS through a canvas, which also needs the bytes. With
 *     a URL we would have to download and cache them on every device anyway,
 *     paying the client storage regardless and adding a server dependency.
 *   • `merchants` is already a synced, mutable table, so this needs no bucket,
 *     no credentials, no signed URLs, no CORS, and no orphan cleanup when a
 *     merchant is deleted.
 *
 * The cost of that choice is that the logo rides in every `merchants` sync
 * pull. One row per tenant under a hard cap makes that a one-time transfer per
 * device, and the cap is what keeps it true — which is the entire job of this
 * file. A merchant photographs their signboard and uploads 4 MB; what gets
 * stored is a few kilobytes.
 *
 * ── Everything is re-encoded, including SVG ────────────────────────────────
 * Whatever is uploaded is decoded and redrawn onto a canvas. That fixes the
 * size, guarantees the result is something both print paths can draw, and
 * means no uploaded file's own bytes are ever stored or handed to a renderer.
 */

/** Widest thermal head we support — 112mm at 832 dots. A4 prints far smaller. */
export const LOGO_MAX_WIDTH = 832;
/** Tall enough for a square mark at full width; most logos are far wider than tall. */
export const LOGO_MAX_HEIGHT = 320;

/**
 * Hard ceiling on the stored data URI.
 *
 * At 32 KB, ten thousand merchants is ~320 MB of text column — less than a
 * week of sync logs — and the per-device sync cost stays negligible.
 */
export const LOGO_MAX_BYTES = 32 * 1024;

/** Refuse to even decode something this large; it is not a logo. */
export const LOGO_MAX_UPLOAD_BYTES = 12 * 1024 * 1024;

export const ACCEPTED_LOGO_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
  'image/gif',
  'image/bmp',
] as const;

/** The `accept` attribute for the file input. */
export const LOGO_ACCEPT = ACCEPTED_LOGO_TYPES.join(',');

export function isSupportedLogoType(mime: string | null | undefined): boolean {
  return !!mime && (ACCEPTED_LOGO_TYPES as readonly string[]).includes(mime.toLowerCase());
}

/**
 * Scale (w, h) down to fit inside the box, preserving aspect ratio.
 *
 * Never scales UP: a 60px logo stays 60px rather than being blown up to 832
 * and stored at eight times the size for no added detail.
 */
export function fitWithin(
  width: number,
  height: number,
  maxWidth = LOGO_MAX_WIDTH,
  maxHeight = LOGO_MAX_HEIGHT,
): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * Bytes a `data:` URI actually occupies, from the base64 payload rather than
 * the string length — the header and the base64 padding both lie by enough to
 * matter against a 32 KB cap.
 */
export function dataUrlByteLength(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  const meta = dataUrl.slice(0, comma);
  const payload = dataUrl.slice(comma + 1);
  if (!meta.includes(';base64')) return payload.length;
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

/** "18 KB" — for telling the shopkeeper what was stored. */
export function describeBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * How we try to get under the cap, in order.
 *
 * PNG first at full size: shop logos are usually flat artwork with few
 * colours, where PNG is both smaller and sharper than anything lossy. The
 * JPEG steps are for the other real case — a photograph of a painted
 * signboard — where PNG is hopeless and a little softness is the right trade.
 *
 * Deliberately NOT WebP: it would be smaller, but this string is read by the
 * Android and iOS apps too, and PNG/JPEG are the two every one of them can
 * decode without question.
 */
export interface EncodeAttempt {
  type: 'image/png' | 'image/jpeg';
  /** Fraction of the fitted size to draw at. */
  scale: number;
  quality?: number;
}

export const ENCODE_ATTEMPTS: readonly EncodeAttempt[] = [
  { type: 'image/png', scale: 1 },
  { type: 'image/png', scale: 0.75 },
  { type: 'image/jpeg', scale: 1, quality: 0.9 },
  { type: 'image/jpeg', scale: 1, quality: 0.75 },
  { type: 'image/jpeg', scale: 0.75, quality: 0.7 },
  { type: 'image/jpeg', scale: 0.5, quality: 0.6 },
];

export interface PreparedLogo {
  /** The `data:` URI to store in merchants.logo_url. */
  dataUrl: string;
  bytes: number;
  width: number;
  height: number;
  /** True when we had to fall back to a lossy encode to fit. */
  lossy: boolean;
}

/** Thrown with a message meant for a shopkeeper, not a developer. */
export class LogoError extends Error {}

/**
 * Decode an uploaded file into something drawable.
 *
 * `createImageBitmap` is the fast path but does not handle SVG everywhere, so
 * an `<img>` with an object URL is the fallback — that one does render SVG,
 * and an `<img>` never executes script inside it.
 */
async function decode(blob: Blob): Promise<{ draw: CanvasImageSource; width: number; height: number }> {
  if (blob.type !== 'image/svg+xml' && typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return { draw: bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      /* fall through to the <img> path */
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new LogoError('That file could not be read as an image.'));
      el.src = url;
    });
    // An SVG with no intrinsic size reports 0×0. Give it the full box rather
    // than failing — that is the size it would be drawn at anyway.
    const width = img.naturalWidth || LOGO_MAX_WIDTH;
    const height = img.naturalHeight || LOGO_MAX_HEIGHT;
    return { draw: img, width, height };
  } finally {
    // Revoking immediately is safe: the bitmap has been decoded by now.
    URL.revokeObjectURL(url);
  }
}

/**
 * Turn an uploaded file into a small, stored-ready `data:` URI.
 *
 * Throws {@link LogoError} with a message that can be shown as-is.
 */
export async function prepareLogo(file: File | Blob): Promise<PreparedLogo> {
  if (!isSupportedLogoType(file.type)) {
    throw new LogoError('Use a PNG, JPG, WebP or SVG image.');
  }
  if (file.size > LOGO_MAX_UPLOAD_BYTES) {
    throw new LogoError(
      `That image is ${describeBytes(file.size)}. Please use one under ${describeBytes(LOGO_MAX_UPLOAD_BYTES)}.`,
    );
  }

  const { draw, width, height } = await decode(file);
  const fitted = fitWithin(width, height);
  if (fitted.width === 0) throw new LogoError('That image appears to be empty.');

  for (const attempt of ENCODE_ATTEMPTS) {
    const w = Math.max(1, Math.round(fitted.width * attempt.scale));
    const h = Math.max(1, Math.round(fitted.height * attempt.scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new LogoError('This browser could not process the image.');

    // Paint white underneath. A transparent PNG flattened by the thermal
    // rasteriser comes out as a black block; on paper the background is white
    // anyway, so this is what the merchant expects to see.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(draw, 0, 0, w, h);

    const dataUrl = canvas.toDataURL(attempt.type, attempt.quality);
    // A browser that cannot encode the requested type silently returns a PNG.
    // Checking the prefix stops us reporting a JPEG fallback that never was.
    const bytes = dataUrlByteLength(dataUrl);
    if (bytes <= LOGO_MAX_BYTES) {
      return {
        dataUrl,
        bytes,
        width: w,
        height: h,
        lossy: dataUrl.startsWith('data:image/jpeg'),
      };
    }
  }

  throw new LogoError(
    `This image is too detailed to store (over ${describeBytes(LOGO_MAX_BYTES)} even after shrinking). A simpler logo, or one with fewer colours, will work.`,
  );
}
